import React from 'react';
import { Check, FileText, Link2, List, Target } from 'lucide-react';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import type { Indicator } from '../../../types/indicator';
import {
  type LstTemplate,
} from '../types';
import { CadeiaAjudaConfig } from './CadeiaAjudaConfig';
import { TplProjectMatrixPanel } from './tpl/TplProjectMatrixPanel';
import type { IndicatorLinkValue } from './IndicatorImpactCombobox';
import { SwiPanelEditor } from './SwiPanelEditor';
import type { SwiFormState } from './SwiConfigSection';

export type ConfigPanelView = 'activities' | 'manager' | 'indicator' | 'swi';

const LOCKED_TAB_HINT = 'Defina o nome da atividade primeiro';

interface ConfigTabProps {
  active: boolean;
  locked: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const ConfigTab: React.FC<ConfigTabProps> = ({ active, locked, onClick, children }) => (
  <button
    type="button"
    title={locked ? LOCKED_TAB_HINT : undefined}
    aria-disabled={locked}
    onClick={locked ? undefined : onClick}
    className={`inline-flex flex-1 items-center justify-center gap-1 border-b-2 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
      active
        ? 'border-[#2F6FED] text-[#2F6FED]'
        : locked
          ? 'cursor-not-allowed border-transparent text-slate-300 dark:text-slate-600'
          : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
    }`}
  >
    {children}
  </button>
);

interface ConfigRightPanelProps {
  view: ConfigPanelView;
  onViewChange: (view: ConfigPanelView) => void;
  scopeLabel: string;
  weekDates: string[];
  templates: LstTemplate[];
  contextUserId?: string;
  selectedTemplateId: string | null;
  onSelectTemplate: (template: LstTemplate) => void;
  onDeactivateTemplate: (id: string) => void;
  members: OrgMember[];
  currentUserId: string;
  isAdmin: boolean;
  savingLink: string | null;
  onSetReportsTo: (userId: string, reportsToUserId: string | null) => Promise<boolean>;
  indicators: Indicator[];
  indicatorLink: IndicatorLinkValue;
  onIndicatorChange: (value: IndicatorLinkValue) => void;
  swiEnabled?: boolean;
  organizationId: string;
  editingTemplateId: string | null;
  swi: SwiFormState;
  onSwiChange: (patch: Partial<SwiFormState>) => void;
  activityTitleReady: boolean;
  editorFocusRequest?: number;
  onBindSwiPattern: () => void;
}

const inputCls =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200';
const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400';

/** Painel contextual direito — listas e seletores sob demanda. */
export const ConfigRightPanel: React.FC<ConfigRightPanelProps> = ({
  view,
  onViewChange,
  scopeLabel,
  weekDates,
  templates,
  contextUserId,
  selectedTemplateId,
  onSelectTemplate,
  onDeactivateTemplate,
  members,
  currentUserId,
  isAdmin,
  savingLink,
  onSetReportsTo,
  indicators,
  indicatorLink,
  onIndicatorChange,
  swiEnabled = true,
  organizationId,
  editingTemplateId,
  swi,
  onSwiChange,
  activityTitleReady,
  editorFocusRequest = 0,
  onBindSwiPattern,
}) => {
  const activeIndicators = indicators.filter((i) => i.status === 'active');
  const hasLink = !!(indicatorLink.linked_indicator_id || indicatorLink.linked_indicator_name?.trim());
  const contextualTabsLocked = !activityTitleReady;

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden border-l border-slate-100 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/20">
      <nav className="flex shrink-0 border-b border-slate-100 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
        <ConfigTab active={view === 'activities'} locked={false} onClick={() => onViewChange('activities')}>
          <List size={12} /> Matriz
        </ConfigTab>
        <ConfigTab
          active={view === 'manager'}
          locked={contextualTabsLocked}
          onClick={() => onViewChange('manager')}
        >
          <Link2 size={12} /> Gestor
        </ConfigTab>
        <ConfigTab
          active={view === 'indicator'}
          locked={contextualTabsLocked}
          onClick={() => onViewChange('indicator')}
        >
          <Target size={12} /> KPI
        </ConfigTab>
        {swiEnabled && (
          <ConfigTab active={view === 'swi'} locked={contextualTabsLocked} onClick={() => onViewChange('swi')}>
            <FileText size={12} /> SWI
          </ConfigTab>
        )}
      </nav>

      <div
        className={`min-h-0 flex-1 ${
          view === 'swi' ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
        }`}
      >
        {view === 'activities' && (
          <TplProjectMatrixPanel
            templates={templates}
            weekDates={weekDates}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={onSelectTemplate}
            onDeactivateTemplate={onDeactivateTemplate}
          />
        )}

        {view === 'manager' && (
          <CadeiaAjudaConfig
            embedded
            members={members}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            savingLink={savingLink}
            onSetReportsTo={onSetReportsTo}
          />
        )}

        {view === 'indicator' && (
          <div className="space-y-3 p-3">
            <p className="text-[11px] text-slate-500">
              Vincule um KPI global ou defina um indicador personalizado para a Nota de Resultado.
            </p>
            <button
              type="button"
              onClick={() =>
                onIndicatorChange({
                  linked_indicator_id: null,
                  linked_indicator_name: null,
                  linked_indicator_target: null,
                })
              }
              className={`w-full rounded-md border px-3 py-2 text-left text-[11px] transition-colors ${
                !hasLink
                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
              }`}
            >
              — Sem indicador (N/A) —
            </button>
            <div>
              <label className={labelCls}>KPIs cadastrados</label>
              <ul className="max-h-[240px] space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900/40">
                {activeIndicators.length === 0 ? (
                  <li className="px-2 py-4 text-center text-[11px] text-slate-400">Nenhum KPI ativo.</li>
                ) : (
                  activeIndicators.map((ind) => {
                    const selected = indicatorLink.linked_indicator_id === ind.id;
                    return (
                      <li key={ind.id}>
                        <button
                          type="button"
                          onClick={() =>
                            onIndicatorChange({
                              linked_indicator_id: ind.id,
                              linked_indicator_name: null,
                              linked_indicator_target:
                                ind.target_value ?? indicatorLink.linked_indicator_target,
                            })
                          }
                          className={`w-full rounded-md px-2 py-2 text-left text-[11px] ${
                            selected
                              ? 'bg-blue-50 font-semibold text-blue-800 dark:bg-blue-950/40 dark:text-blue-200'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                        >
                          <span>{ind.name}</span>
                          {ind.target_value != null && (
                            <span className="ml-1 text-slate-400">
                              · meta {ind.target_value}
                              {ind.unit}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
            <div>
              <label className={labelCls}>Indicador personalizado</label>
              <input
                className={inputCls}
                value={indicatorLink.linked_indicator_name ?? ''}
                placeholder="Ex.: OEE linha 3"
                onChange={(e) =>
                  onIndicatorChange({
                    linked_indicator_id: null,
                    linked_indicator_name: e.target.value || null,
                    linked_indicator_target: indicatorLink.linked_indicator_target,
                  })
                }
              />
            </div>
            {hasLink && (
              <div>
                <label className={labelCls}>
                  Meta numérica <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={0}
                  step="any"
                  className={inputCls}
                  value={indicatorLink.linked_indicator_target ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const num = raw === '' ? null : Number(raw);
                    onIndicatorChange({
                      ...indicatorLink,
                      linked_indicator_target: num != null && Number.isFinite(num) ? num : null,
                    });
                  }}
                  placeholder="Ex.: 85 ou 2"
                />
              </div>
            )}
          </div>
        )}

        {view === 'swi' && swiEnabled && (
          <div className="flex min-h-0 flex-1 flex-col p-3">
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 pb-2 dark:border-slate-800">
              <p className="min-w-0 text-[11px] text-slate-500">
                Instrução de Trabalho Padrão — edite o passo a passo da rotina em foco.
              </p>
              <button
                type="button"
                onClick={onBindSwiPattern}
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60"
              >
                <Check size={12} /> Vincular Padrão
              </button>
            </div>
            <SwiPanelEditor
              organizationId={organizationId}
              templateId={editingTemplateId}
              value={swi}
              onChange={onSwiChange}
              fillHeight
              editorFocusRequest={editorFocusRequest}
            />
          </div>
        )}
      </div>
    </aside>
  );
};

export default ConfigRightPanel;
